import React, { useMemo, useState } from 'react';
import * as Sentry from '@sentry/react';
import {
  GENERIC_PAYMENT_ERROR,
  PAYMENT_METHODS,
  PAYMENT_PROCESSOR,
  PAYMENT_PROVIDER,
} from '../../constants';
import { payCheck } from '../../../../store/slices/payment/paymentThunk';
import Check from '../check/check';
import StripeElement from '../../stripe/StripeElement';
import apiAccess from '../../../../api/apiAccess';
import { XpressBillPay } from '../../xpressbillpay';
import { RedirectBasedPayment } from './RedirectBasedPayment';
import { PaymentFees, PaymentMethod } from 'src/types/payment';
import { useAppDispatch } from '../../../../store/store';
import { calculateTotalDue, isGeneric } from '../utils';
import { handlePostRedirect } from './customHandlers';
import { DeckardError } from '../../../../types/errors';

export interface PaymentFormProps {
  fees: PaymentFees;
  flow: string;
  onSuccess: (isGeneric?: boolean) => void;
  paymentID: string;
  paymentMethod: PaymentMethod;
  paymentProvider?: PAYMENT_PROVIDER;
  permitIdentifier: string;
  processor: PAYMENT_PROCESSOR;
  signatoryName: string;
  userEmail: string;
}

export const PaymentForm = ({
  fees,
  flow,
  onSuccess,
  paymentID,
  paymentMethod,
  paymentProvider,
  permitIdentifier,
  processor,
  signatoryName,
  userEmail,
}: PaymentFormProps) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const dispatch = useAppDispatch();

  const isGenericProvider = useMemo(() => isGeneric(processor), [processor]);

  const getPaymentApi = (
    paymentMethod: PaymentMethod,
    processor: PAYMENT_PROCESSOR
  ) => {
    switch (paymentMethod) {
      case PAYMENT_METHODS.CHECK:
        return async () => dispatch(payCheck());
      case PAYMENT_METHODS.CREDIT_OR_DEBIT__CARD:
        if (processor === PAYMENT_PROCESSOR.STRIPE) {
          return apiAccess.payByCreditCard;
        }
        return apiAccess.payGeneric;
      case PAYMENT_METHODS.ACH_DIRECT_DEBIT:
        if (processor === PAYMENT_PROCESSOR.STRIPE) {
          return apiAccess.payByACHDirectDebit;
        }
        return apiAccess.payGeneric;
      default:
        return undefined;
    }
  };

  const totalDue = useMemo(
    () => calculateTotalDue(paymentMethod, fees, isGenericProvider),
    [fees, paymentMethod, isGenericProvider]
  );

  const handlePayment = async () => {
    setLoading(true);
    const paymentApi = getPaymentApi(paymentMethod, processor);
    try {
      if (paymentApi) {
        const response = await paymentApi(paymentID, signatoryName, userEmail);
        onSuccess(isGenericProvider && paymentMethod !== PAYMENT_METHODS.CHECK);
        return response;
      }
      setError(GENERIC_PAYMENT_ERROR);
    } catch (err) {
      const errorResponse = (
        err as { response?: { data?: DeckardError; status?: number } }
      )?.response;
      // Backend rejects a payGeneric request with a 409 when the payment session
      // rolled over into a new day; that response carries a user-friendly message.
      if (errorResponse?.status === 409 && errorResponse?.data?.Message) {
        setError(errorResponse.data.Message);
      } else {
        setError(GENERIC_PAYMENT_ERROR);
      }
      Sentry.captureException(err);
    } finally {
      setLoading(false);
    }
  };

  const handleGenericPayment = async () => {
    const response = await handlePayment();
    if (response?.checkout_mode === 'post_redirect') {
      handlePostRedirect(response, paymentProvider);
    } else if (response?.url?.startsWith('http')) {
      window.open(response.url, '_blank', 'toolbar=0,location=0,menubar=0');
    }
    return response;
  };

  if (paymentMethod === PAYMENT_METHODS.CHECK) {
    return (
      <Check
        onPay={handlePayment}
        loading={loading}
        error={error}
      />
    );
  } else if (processor === PAYMENT_PROCESSOR.STRIPE) {
    return (
      <StripeElement
        flow={flow}
        onPay={handlePayment}
        paymentMethod={paymentMethod}
      />
    );
  } else if (paymentProvider === PAYMENT_PROVIDER.XPRESS_BILL_PAY) {
    return (
      <XpressBillPay
        onPay={handlePayment}
        permitIdentifier={permitIdentifier}
        totalDue={totalDue}
        loading={loading}
        error={error}
      />
    );
  } else if (isGenericProvider) {
    return (
      <RedirectBasedPayment
        dataTestId="generic-form-component"
        error={error}
        loading={loading}
        onClickCallback={handleGenericPayment}
        totalDue={totalDue}
      >
        The button below will take you to our third-party payment system to make
        your payment.
      </RedirectBasedPayment>
    );
  }

  return <></>;
};
